import { PATHS as SHARED_PATHS } from '@shared/router/paths';
import { FINANCE_APP_PATHS } from '@shared/config/cross-app-paths';

/**
 * Finance routes. Shared auth / landing paths come from the foundation; every
 * screen here belongs to the FINANCE application (finance-service, plus the
 * access administration it forwards to identity-service).
 *
 * Static segments (`new`, `create`, `edit`, `preview`) out-rank the `:id`
 * routes beside them, so they can never be read as ids.
 */
export const PATHS = {
  ...SHARED_PATHS,
  app: {
    ...SHARED_PATHS.app,
    dashboard: '/dashboard',

    // ------------------------------------------------- company master data
    companies: '/companies',
    companyNew: '/companies/new',
    companyDetail: (companyId = ':companyId') => `/companies/${companyId}`,
    companyEdit: (companyId = ':companyId') => `/companies/${companyId}/edit`,
    vendorCategories: '/master-data/vendor-categories',
    assetCategories: '/master-data/asset-categories',

    // ------------------------------------------------------------- clients
    clients: '/clients',
    clientNew: '/clients/new',
    clientDetail: (clientId = ':clientId') => `/clients/${clientId}`,
    clientEdit: (clientId = ':clientId') => `/clients/${clientId}/edit`,

    // ----------------------------------------------------------- invoicing
    invoices: '/invoicing/invoices',
    invoiceCreate: '/invoicing/invoices/create',
    invoiceDetail: (invoiceId = ':invoiceId') => `/invoicing/invoices/${invoiceId}`,
    invoiceEdit: (invoiceId = ':invoiceId') => `/invoicing/invoices/${invoiceId}/edit`,
    /** Unsaved-form preview, opened in its own tab (outside the shell). */
    invoicePreview: (previewId = ':previewId') => `/invoicing/invoices/preview/${previewId}`,
    invoiceTemplates: '/invoicing/templates',
    invoiceNumbering: '/invoicing/bill-numbering',
    invoiceItems: '/invoicing/items',
    invoiceItemCreate: '/invoicing/items/create',
    invoiceItemEdit: (itemId = ':itemId') => `/invoicing/items/${itemId}/edit`,
    invoiceTaxes: '/invoicing/taxes',
    invoiceTaxCreate: '/invoicing/taxes/create',
    invoiceTaxView: (taxId = ':taxId') => `/invoicing/taxes/${taxId}`,
    invoiceTaxEdit: (taxId = ':taxId') => `/invoicing/taxes/${taxId}/edit`,

    // -------------------------------------------------- finance & accounts
    financeOverview: '/finance/overview',
    // Vendor Management (Vendor Master, Services & Subscriptions — its tabs)
    // and two Accounts Payable screens that keep their addresses under the
    // same prefix: Supplier Bills (expenses → /payables) and the Payment
    // Register (payments → /ledger). Keys keep the API resource names.
    // Earlier addresses redirect (routes.tsx, LEGACY_REDIRECTS).
    financeVendors: '/finance/vendor-management/vendors',
    financeVendorNew: '/finance/vendor-management/vendors/new',
    financeVendorDetail: (vendorId = ':vendorId') => `/finance/vendor-management/vendors/${vendorId}`,
    financeVendorEdit: (vendorId = ':vendorId') => `/finance/vendor-management/vendors/${vendorId}/edit`,
    financeServices: '/finance/vendor-management/services',
    /** `?vendor_id=` preselects the vendor. */
    financeServiceNew: '/finance/vendor-management/services/new',
    financeServiceDetail: (serviceId = ':serviceId') => `/finance/vendor-management/services/${serviceId}`,
    financeServiceEdit: (serviceId = ':serviceId') => `/finance/vendor-management/services/${serviceId}/edit`,
    /** Record this period's bill from a service (creates one payable). */
    financeServiceBill: (serviceId = ':serviceId') => `/finance/vendor-management/services/${serviceId}/bill`,
    /**
     * Supplier Bills (Accounts Payable). The old `?view=receivables` address
     * redirects to the Receivables Aging (receivables live under Billing).
     */
    financeExpenses: '/finance/vendor-management/payables',
    financeExpenseNew: '/finance/vendor-management/payables/new',
    financeExpenseDetail: (expenseId = ':expenseId') => `/finance/vendor-management/payables/${expenseId}`,
    financeExpenseEdit: (expenseId = ':expenseId') => `/finance/vendor-management/payables/${expenseId}/edit`,
    financeExpensePay: (expenseId = ':expenseId') =>
      `/finance/vendor-management/payables/${expenseId}/payments/new`,
    financePaymentDetail: (expenseId = ':expenseId', paymentId = ':paymentId') =>
      `/finance/vendor-management/payables/${expenseId}/payments/${paymentId}`,
    /** The Payment Register — every payment made against a bill. */
    financePayments: '/finance/vendor-management/ledger',
    financeReimbursements: '/finance/reimbursements',
    financeReimbursementDetail: (paymentId = ':paymentId') => `/finance/reimbursements/${paymentId}`,

    financeAssetOverview: '/finance/asset-management/overview',
    financeAssets: FINANCE_APP_PATHS.assets,
    financeAssetNew: '/finance/asset-management/assets/new',
    financeAssetDetail: (assetId = ':assetId') => FINANCE_APP_PATHS.assetDetail(assetId),
    financeAssetEdit: (assetId = ':assetId') => `/finance/asset-management/assets/${assetId}/edit`,
    financeAssetAssign: (assetId = ':assetId') => `/finance/asset-management/assets/${assetId}/assign`,
    financeAssetTransfer: (assetId = ':assetId') => `/finance/asset-management/assets/${assetId}/transfer`,
    financeAssetReturn: (assetId = ':assetId') => `/finance/asset-management/assets/${assetId}/return`,
    financeAssetDispose: (assetId = ':assetId') => `/finance/asset-management/assets/${assetId}/dispose`,
    financeAssetAssignments: '/finance/asset-management/assignments',
    financeAssetRepairs: '/finance/asset-management/repairs',
    /** `?asset_id=` preselects the asset. */
    financeAssetRepairNew: '/finance/asset-management/repairs/new',
    financeAssetRepairDetail: (repairId = ':repairId') => `/finance/asset-management/repairs/${repairId}`,
    financeAssetRepairEdit: (repairId = ':repairId') => `/finance/asset-management/repairs/${repairId}/edit`,
    financeAssetRepairComplete: (repairId = ':repairId') =>
      `/finance/asset-management/repairs/${repairId}/complete`,
    financeAssetDisposals: '/finance/asset-management/disposals',

    /** Accounting dashboard (ledger figures); the vendor Overview above stays. */
    financeDashboard: '/finance/dashboard',
    /** Renewal forecast of the active services (Services & Subscriptions). */
    financeServiceForecast: '/finance/vendor-management/services/forecast',

    // Payables beyond the bill register: vendor payments, credits, aging.
    financeVendorPayments: '/finance/payables/vendor-payments',
    /** `?vendor_id=` / `?company_id=` preselect. */
    financeVendorPaymentNew: '/finance/payables/vendor-payments/new',
    financeVendorPaymentDetail: (paymentId = ':paymentId') =>
      `/finance/payables/vendor-payments/${paymentId}`,
    financeVendorRefunds: '/finance/payables/vendor-refunds',
    financeVendorCredits: '/finance/payables/vendor-credits',
    /** `?vendor_id=` / `?expense_id=` preselect. */
    financeVendorCreditNew: '/finance/payables/vendor-credits/new',
    financeVendorCreditDetail: (creditId = ':creditId') =>
      `/finance/payables/vendor-credits/${creditId}`,
    financeVendorCreditEdit: (creditId = ':creditId') =>
      `/finance/payables/vendor-credits/${creditId}/edit`,
    financeApAging: '/finance/payables/aging',
    /** `?vendor_id=&company_id=` opens one vendor's statement from the ledger. */
    financeVendorLedger: '/finance/payables/vendor-ledger',

    // ---------------------------------------------------------- accounting
    accountingSetup: '/finance/accounting/setup',
    accountingAccounts: '/finance/accounting/accounts',
    accountingJournal: '/finance/accounting/journal',
    accountingJournalNew: '/finance/accounting/journal/new',
    accountingJournalEntry: (entryId = ':entryId') => `/finance/accounting/journal/${entryId}`,
    accountingJournalEdit: (entryId = ':entryId') => `/finance/accounting/journal/${entryId}/edit`,
    /** `?account_id=` opens one account. */
    accountingGeneralLedger: '/finance/accounting/general-ledger',
    accountingPeriods: '/finance/accounting/periods',
    accountingOpeningBalances: '/finance/accounting/opening-balances',
    accountingOpeningNew: '/finance/accounting/opening-balances/new',
    accountingOpeningReconciliation: '/finance/accounting/opening-balances/reconciliation',
    accountingOpeningBatch: (batchId = ':batchId') =>
      `/finance/accounting/opening-balances/${batchId}`,
    accountingProfitLoss: '/finance/accounting/reports/profit-and-loss',
    accountingBalanceSheet: '/finance/accounting/reports/balance-sheet',
    accountingCashFlow: '/finance/accounting/reports/cash-flow',
    accountingTrialBalance: '/finance/accounting/reports/trial-balance',
    accountingControlReconciliation: '/finance/accounting/reports/control-reconciliation',
    accountingTaxSummary: '/finance/accounting/reports/tax-summary',
    accountingImports: '/finance/accounting/imports',
    accountingImportNew: '/finance/accounting/imports/new',
    accountingImport: (importId = ':importId') => `/finance/accounting/imports/${importId}`,

    // ------------------------------------------------------------ treasury
    treasuryCashPosition: '/finance/treasury/cash-position',
    /** `?account_id=` opens one account. */
    treasuryReconciliation: '/finance/treasury/reconciliation',
    /** Imported bank statements; `?account_id=` narrows to one account. */
    treasuryStatements: '/finance/treasury/statements',
    /** `?account_id=` preselects the account. */
    treasuryStatementImport: '/finance/treasury/statements/import',
    treasuryStatement: (statementId = ':statementId') => `/finance/treasury/statements/${statementId}`,
    treasuryTransfers: '/finance/treasury/transfers',
    treasuryTransferNew: '/finance/treasury/transfers/new',
    treasuryTransfer: (transferId = ':transferId') => `/finance/treasury/transfers/${transferId}`,
    treasuryTransferEdit: (transferId = ':transferId') =>
      `/finance/treasury/transfers/${transferId}/edit`,
    treasuryLoans: '/finance/treasury/loans',
    treasuryLoanNew: '/finance/treasury/loans/new',
    treasuryLoan: (loanId = ':loanId') => `/finance/treasury/loans/${loanId}`,
    treasuryLoanEdit: (loanId = ':loanId') => `/finance/treasury/loans/${loanId}/edit`,
    treasuryInvestments: '/finance/treasury/investments',
    treasuryInvestmentNew: '/finance/treasury/investments/new',
    treasuryInvestment: (investmentId = ':investmentId') =>
      `/finance/treasury/investments/${investmentId}`,
    treasuryInvestmentEdit: (investmentId = ':investmentId') =>
      `/finance/treasury/investments/${investmentId}/edit`,

    // --------------------------------------------------------- receivables
    receipts: '/receivables/receipts',
    /** `?client_id=` / `?company_id=` / `?invoice_id=` preselect. */
    receiptNew: '/receivables/receipts/new',
    receipt: (receiptId = ':receiptId') => `/receivables/receipts/${receiptId}`,
    customerRefunds: '/receivables/refunds',
    creditNotes: '/receivables/credit-notes',
    /** `?invoice_id=` preselects the invoice (and its client and company). */
    creditNoteNew: '/receivables/credit-notes/new',
    creditNote: (creditNoteId = ':creditNoteId') => `/receivables/credit-notes/${creditNoteId}`,
    creditNoteEdit: (creditNoteId = ':creditNoteId') =>
      `/receivables/credit-notes/${creditNoteId}/edit`,
    arAging: '/receivables/aging',
    /** `?client_id=&company_id=` opens one statement. */
    customerLedger: '/receivables/customer-ledger',

    // ------------------------------------------------------------- employees
    // People / HRMS data, read and edited through those services' own APIs.
    employees: '/employees',
    employeeDetail: (employeeId = ':employeeId') => `/employees/${employeeId}`,
    employeeEdit: (employeeId = ':employeeId') => `/employees/${employeeId}/edit`,

    // ------------------------------------------------- access administration
    accessUsers: '/access/users',
    accessUserNew: '/access/users/new',
    accessUserDetail: (userId = ':userId') => FINANCE_APP_PATHS.accessUser(userId),
    roles: '/access/roles',
    permissions: '/access/permissions',
    /** Employee → Permissions (roles, Finance grants, effective access, history). */
    employeePermissions: (employeeId = ':employeeId') => FINANCE_APP_PATHS.employeePermissions(employeeId),

    // ---------------------------------------------------------------- audit
    auditLogs: '/audit/logs',
  },
} as const;
